/**
 * @file StatementRunFrame.js
 * @version 2.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-20
 * @modified 2026-10-20
 *
 * The frame every Generate Statements step sits in (Cam, 20 Oct 2026 —
 * Statement Runs Redesign: "a lot taking up vertical space … the button
 * positioning (next/previous step buttons especially) is not right, and the
 * vertical scrolling is problematic"):
 *
 *   - one header: the run's name, its kind and a meta line; a connected,
 *     numbered stepper; the run's people and its tools;
 *   - one line per notice (hand-overs, who is on the run);
 *   - one quiet row of check chips, coloured only when they need something —
 *     each opens its detail instead of filling the page;
 *   - a body of cards that fills the window; the cards scroll inside it;
 *   - a fixed footer: Back always bottom-left, the one primary action always
 *     bottom-right, and between them an icon and a line saying what is in the
 *     way (with a progress bar on Review).
 *
 * 2.0.0 (2026-10-20, Cam — the statements look): the header, stepper, chips
 * and footer rebuilt to the approved redesign; `meta`, `people` and
 * `footer.progress` added.
 */

import React from 'react';
import { useSlideOutButton } from './SlideOutButton';
import './StatementRun.css';

const STATUS_ICON = { bad: 'error', warn: 'priority_high', ok: 'check' };

/**
 * @param {object} props
 * @param {string} props.title
 * @param {string} [props.meta] - "1–30 Sep 2026 · 20 statements"
 * @param {{ label: string, interim: boolean }|null} props.kind
 * @param {Array<{ n: number, name: string, reachable: boolean, why?: string }>} props.steps
 * @param {number} props.step
 * @param {function} props.onStep
 * @param {React.ReactNode} [props.people] - who is on the run (avatars)
 * @param {React.ReactNode} props.tools - slide-out buttons, each in its own <div>
 * @param {Array<{ key, icon, label, tone?, onClick, title?, spin? }>} props.checks
 * @param {React.ReactNode} [props.notices]
 * @param {{ back?: object, status?: React.ReactNode, statusTone?: string, statusIcon?: string, progress?: number|null, next?: object, extra?: React.ReactNode }} props.footer
 */
function StatementRunFrame({ title, meta, kind, steps, step, onStep, people = null, tools, checks = [], notices = null, footer = {}, children }) {
  const { back, next, status, statusTone, statusIcon, progress, extra } = footer;
  useSlideOutButton([step, back?.label, next?.label, next?.disabled, back?.disabled, checks.length]);
  return (
    <div className="sr-frame">
      <header className="sr-header">
        <div className="sr-title">
          <div className="sr-title-row">
            <span className="sr-run-name">{title}</span>
            {kind && (
              <span className={`sr-badge sr-kind ${kind.interim ? 'solid primary interim' : 'outline'}`}>
                <span className="material-symbols-rounded">{kind.interim ? 'history_edu' : 'calendar_month'}</span>
                {kind.label}
              </span>
            )}
          </div>
          {meta && <span className="sr-run-meta">{meta}</span>}
        </div>
        <nav className="sr-stepper" aria-label="Steps">
          <ol>
            {steps.map((s, i) => (
              <React.Fragment key={s.n}>
                {i > 0 && <li aria-hidden="true" className={`sr-step-line${s.n <= step ? ' done' : ''}`} />}
                <li>
                  <button
                    type="button"
                    className={`sr-step${s.n === step ? ' on' : ''}${s.n < step ? ' done' : ''}`}
                    onClick={() => s.reachable && s.n !== step && onStep(s.n)}
                    disabled={!s.reachable}
                    aria-current={s.n === step ? 'step' : undefined}
                    title={s.reachable ? s.name : (s.why || 'Not yet')}
                  >
                    <span className="sr-step-n">{s.n < step ? <span className="material-symbols-rounded">check</span> : s.n}</span>
                    <span className="sr-step-name">{s.name}</span>
                  </button>
                </li>
              </React.Fragment>
            ))}
          </ol>
        </nav>
        <div className="sr-tools">
          {people}
          <div className="buttons slideout">{tools}</div>
        </div>
      </header>

      <div className="sr-notices">{notices}</div>

      {checks.length > 0 && (
        <div className="sr-checks" role="group" aria-label="Checks">
          <span className="sr-checks-label">Checks</span>
          {checks.map(c => (
            <button
              key={c.key}
              type="button"
              className={`sr-chip${c.tone ? ` ${c.tone}` : ''}`}
              onClick={c.onClick}
              title={c.title || c.label}
              disabled={!c.onClick}
            >
              {c.icon && <span className={`material-symbols-rounded${c.spin ? ' stmt-integrity-spin' : ''}`}>{c.icon}</span>}
              <span>{c.label}</span>
            </button>
          ))}
        </div>
      )}

      <div className="sr-body">{children}</div>

      <footer className="sr-footer">
        <div className="sr-footer-left buttons slideout">
          {back && (
            <div>
              <button type="button" className="btn-slideout always-out" onClick={back.onClick} disabled={back.disabled} title={back.title}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">arrow_back</span></div>
                <span className="btn-label">{back.label}</span>
              </button>
            </div>
          )}
        </div>
        <div className={`sr-footer-status${statusTone ? ` ${statusTone}` : ''}`} role="status">
          {status ? (
            <span className={`sr-dot${statusTone ? ` ${statusTone}` : ''}`} aria-hidden="true">
              <span className="material-symbols-rounded">{statusIcon || STATUS_ICON[statusTone] || 'info'}</span>
            </span>
          ) : null}
          <span className="sr-footer-text">{status}</span>
          {progress != null && (
            <span className="sr-progress" role="progressbar" aria-label="Reviewed" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
              <i style={{ width: `${progress}%` }} />
            </span>
          )}
        </div>
        <div className="sr-footer-right buttons slideout">
          {extra}
          {next && (
            <div>
              <button
                type="button"
                className="btn-slideout btn-slideout-primary always-out"
                onClick={next.onClick}
                disabled={next.disabled}
                title={next.title || undefined}
                data-write-action={next.writeAction ? true : undefined}
              >
                <div className="icon-wrapper"><span className="material-symbols-rounded">{next.icon || 'arrow_forward'}</span></div>
                <span className="btn-label">{next.label}</span>
              </button>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}

export default StatementRunFrame;
